'use client';

import React from 'react';

import * as Sentry from '@sentry/nextjs';
import { Formik } from 'formik';
import { useLocale, useTranslations } from 'next-intl';

import { THUMB_STATE } from '../constants';
import { useFeedbackFormContext } from '../Form/FeedbackFormContext';
import { OrangeTextThanks } from '../Form/Form.styled';
import IntlText from '../IntlText';
import messages from '../messages';
import Form from './Form';
import { log } from '@/utils/logger';
import request from '@/utils/queryData/rest/clientRequest';

const sendPostRequest = ({ body, callback, failCallback }) => {
  // Convert URLSearchParams to string for form-encoded body
  const formDataString = body.toString();

  new request(`feedback`)
    .post({
      body: formDataString,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
    .then(data => {
      if (!data) {
        log.error('No data');
        return null;
      }

      if ('status' in data) {
        if (typeof callback === 'function' && data.status === 'ok') {
          callback();
        } else {
          failCallback();
        }
      }

      log.error('No status');
    })
    .catch(error => {
      console.error(error);
      Sentry.captureException(error);
      failCallback();
    });
};

interface FormValues {
  feedback_canned_response: string;
  feedback_message: string;
}

type FormErrors = {
  feedback_error?: string;
};

/**
 * @todo text
 */
export default function SecondStep() {
  const { thumbState, entityId, isSent, setIsSent } = useFeedbackFormContext();
  const locale = useLocale();
  const translate = useTranslations();

  const initialValues: FormValues = React.useMemo(
    () => ({
      feedback_canned_response: '',
      feedback_message: '',
    }),
    []
  );

  const validate = React.useCallback((values: FormValues): FormErrors => {
    const errors: FormErrors = {};

    if (!values?.feedback_canned_response && !values?.feedback_message) {
      errors.feedback_error = translate(messages.validation);
    }

    return errors;
  }, []);

  const onSubmit = React.useCallback(
    (values, { setErrors }) => {
      const bodyContent = new URLSearchParams({
        lng: locale,
        object_id: entityId !== undefined ? String(entityId) : '',
        object_url: typeof window !== 'undefined' && window.location.href ? String(window.location.href) : '',
        reason: values.feedback_canned_response,
        feedback: thumbState === THUMB_STATE.UP ? '1' : '0',
        message: values.feedback_message,
      });

      sendPostRequest({
        body: bodyContent,
        callback: () => setIsSent(true),
        failCallback: () => setErrors({ feedback_error: translate(messages.error) }),
      });
    },
    [entityId, thumbState]
  );

  if (!isSent) {
    return (
      <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
        {formikProps => <Form {...formikProps} />}
      </Formik>
    );
  }

  return (
    <OrangeTextThanks>
      {thumbState && <IntlText index={thumbState === THUMB_STATE.UP ? messages.thanks : messages.thanksWithFollowup} />}
    </OrangeTextThanks>
  );
}
